import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { RefreshTokenRequest } from '@lib/interfaces/auth.interface';
import { AuthService } from '@lib/services';
import { TokenRefreshService } from '@lib/services/auth/token-refresh.service';
import { clearAppSession, selectAppSession, setAppSession } from '@lib/store';
import { Store } from '@ngrx/store';
import { catchError, filter, switchMap, take, throwError } from 'rxjs';

/**
 * Interceptor that adds an Authorization header to requests that are authenticated and target the API URL.
 * Handles 401 errors by calling the refresh token endpoint and updating the session in the store.
 */
export const jwtInterceptor: HttpInterceptorFn = (request, next) => {
    const authService = inject(AuthService);
    const store = inject(Store);
    const router = inject(Router);
    const tokenRefreshService = inject(TokenRefreshService);

    const isApiRequest = request.url.startsWith(environment.API_URL);

    return store.select(selectAppSession).pipe(
        take(1),
        switchMap((appSession) => {
            let req = request;
            if (authService.isAuthenticated && isApiRequest && appSession?.accessToken) {
                req = request.clone({
                    setHeaders: {
                        Authorization: `Bearer ${appSession.accessToken}`,
                    },
                });
            }
            return next(req).pipe(
                catchError((error) => {
                    if (error.status === 401 && appSession?.refreshToken) {
                        // Check if the error is from the refresh-token endpoint
                        if (request.url.includes('/auth/refresh-token')) {
                            tokenRefreshService.reset();
                            store.dispatch(clearAppSession());
                            router.navigate(['auth/login']);
                            return throwError(() => error);
                        }

                        if (!tokenRefreshService.isRefreshing) {  // NOSONAR
                            tokenRefreshService.isRefreshing = true;
                            tokenRefreshService.refreshTokenSubject.next(null);
                            const refreshRequest: RefreshTokenRequest = {
                                Authorization: `Bearer ${appSession.accessToken}`,
                                refreshToken: appSession.refreshToken,
                            };
                            return authService.refreshToken(refreshRequest).pipe(
                                switchMap((response) => {
                                    tokenRefreshService.isRefreshing = false;
                                    tokenRefreshService.refreshTokenSubject.next(response.data.accessToken);
                                    store.dispatch(setAppSession({ session: { ...appSession, ...response.data } }));
                                    const retriedReq = req.clone({
                                        setHeaders: {
                                            Authorization: `Bearer ${response.data.accessToken}`,
                                        },
                                    });
                                    return next(retriedReq);
                                }),
                                catchError((refreshError) => {
                                    tokenRefreshService.reset();
                                    store.dispatch(clearAppSession());
                                    router.navigate(['auth/login']);
                                    return throwError(() => refreshError); // NOSONAR
                                }),
                            );
                        } else {
                            return tokenRefreshService.refreshTokenSubject.pipe(
                                filter((token) => token != null),
                                take(1),
                                switchMap((token) => {
                                    const retriedReq = req.clone({
                                        setHeaders: {
                                            Authorization: `Bearer ${token}`,
                                        },
                                    });
                                    return next(retriedReq);
                                }),
                            );
                        }
                    }
                    return throwError(() => error);
                }),
            );
        }),
    );
};
